/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Observable } from 'rxjs';

import { ICanvasLimits, maxCanvasSize } from '../../../app-data/constants/max-canvas-size';
import { IExportContext } from '@appCanvasCommon/export/IExportContext';
import { Lightbox } from '@appCanvasCommon/export/Lightbox';

/**
 * Converts an HTML canvas element to a PNG blob and automatically downloads it to the user's device.
 * @example
 * ```typescript
 * // Export a plan canvas with custom filename
 * const planCanvas = document.getElementById('planCanvas') as HTMLCanvasElement;
 * convertCanvasToBlobAndDownload(planCanvas);
 * ```
 * @param canvas - The HTML canvas element containing the image to blob-ify
 * @param filename - The desired filename for the downloaded file (defaults to 'export.png')
 */
export const convertCanvasToBlobAndDownload = (
	canvas: HTMLCanvasElement,
	filename: string = `export.png`
): Observable<void> => {
	return new Observable<void>((subscriber) => {
		canvas.toBlob((blob: Blob | null) => {
			try {
				if (!blob) {
					subscriber.error(
						new Error('Failed to convert canvas to blob. Canvas may be too large or corrupted.')
					);
					return;
				}

				downloadAsBlob(blob, filename);

				subscriber.next();
				subscriber.complete();
			} catch (error) {
				subscriber.error(error);
			}
		});
	});
};

/**
 * Converts an HTML canvas element to a PNG Blob as an Observable.
 * This function wraps the asynchronous `canvas.toBlob()` API in an RxJS Observable, making it easier to chain in RXJS pipelines.
 */
export function convertCanvasToBlob(canvas: HTMLCanvasElement): Observable<Blob> {
	return new Observable<Blob>((subscriber) => {
		canvas.toBlob((blob) => {
			try {
				if (!blob) {
					subscriber.error(new Error('Failed to convert canvas to blob.'));
				} else {
					subscriber.next(blob);
					subscriber.complete();
				}
			} catch (error) {
				subscriber.error(error);
			}
		});
	});
}

/**
 * Downloads a Blob object as a file to the user's device by creating a temporary download link.
 * This function creates an invisible anchor element, sets up the download attributes,
 * programmatically clicks it to trigger the download, then cleans up the temporary elements.
 * @example
 * ```typescript
 * // Download an image blob (typically from canvas.toBlob())
 * downloadAsBlob(imageBlob, 'plan-export.png');
 * ```
 * @param blob - The Blob object containing the file data to download
 * @param filename - The filename that will be suggested to the user for the download
 */
export const downloadAsBlob = (blob: Blob, filename: string): void => {
	const a: HTMLAnchorElement = document.createElement('a');
	a.href = window.URL.createObjectURL(blob);
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
};
/**
 * Helper method to determine if the provided width and height exceed the width, height and area canvas limits
 * @param width The width of the expected canvas element
 * @param height The height of the expected canvas element
 * @param limits The maximum canvas limits to compare again. Defaults to Chrome/Safari/Opera nax limits
 */
export const doesExceedCanvasLimits = (
	width: number,
	height: number,
	limits: ICanvasLimits = maxCanvasSize
): boolean => {
	const area: number = width * height;
	return area >= limits.maxArea || width >= limits.maxWidth || height >= limits.maxHeight;
};

/**
 * Creates an export context object for the current tile being captured
 */
export const createExportContext = (
	tileX: number,
	tileY: number,
	tilesX: number,
	tilesY: number,
	tileWorldX: number,
	tileWorldY: number,
	tileWidth: number,
	tileHeight: number,
	lightbox: Lightbox
): IExportContext => {
	return {
		tileX,
		tileY,
		tilesX,
		tilesY,
		tileWorldX,
		tileWorldY,
		tileWidth,
		tileHeight,
		lightbox,
		isFirstTileInRow: tileX === 0,
		isFirstTileInColumn: tileY === 0,
		isFirstRow: tileY === 0,
		isLastTileInRow: tileX === tilesX - 1,
		isLastTileInColumn: tileY === tilesY - 1,
		tileIndex: tileY * tilesX + tileX,
	};
};
